import React, { useMemo, useRef, useState, useEffect, useCallback } from 'react';
import { Typography, Tooltip, Button, Modal, Input, Form, Spin, notification, Table, DatePicker } from 'antd';
import { ArrowLeftOutlined, CaretDownOutlined, CaretUpOutlined, EditOutlined, FileExcelOutlined, ShareAltOutlined, CloudUploadOutlined, ToolOutlined, CloseOutlined, CalendarOutlined } from '@ant-design/icons';
import { Button as CrcButton, Checkbox, Spinner } from '@crc-evans/ui-components';
import { FileSpreadsheet, LayoutTemplate, Plus, Upload } from 'lucide-react';
import history from '../../services/history';
import { AgGridReact } from 'ag-grid-react';
import moment from 'moment';
import ExcelJS from 'exceljs';
import * as XLSX from 'xlsx';
import { connect } from 'react-redux';
import { get } from 'lodash';
import { fetchMaterials, bulkUploadMaterials, shareMaterial } from '../../redux/materials/action';
import axiosConfig from '../../services/axiosConfig';
import MaterialsModal from './MaterialsModal';
import { downloadDataAsExcel } from '../../utils/downloadExcelUtils';
import { PDFDocument, StandardFonts, rgb } from 'pdf-lib';
import * as pdfjsLib from 'pdfjs-dist';
import { wrapText } from '../../utils/pdfUtils';
import { generateCombinedPdfReport } from '../../utils/combinedPdfReportUtils';
import { useHeatStyleColumnFacets } from '../technical/heatStyleColumnFacets';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-material.css';
import 'ag-grid-enterprise';

// Set worker path for PDF.js
pdfjsLib.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.js`;

const { Title, Text } = Typography;
const MATERIALS_BULK_UPLOAD_ACCEPT =
  '.xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel';

const MATERIALS_DATE_COLUMN_KEYS = ['RevisionDate', 'FormerDate'];

/** Text columns in modern table (same as `columnList` minus ID and date columns) for Heat-style facets. */
const MATERIALS_TEXT_FACET_COLUMNS = [
  'MaterialCategory',
  'Name',
  'ProductCode',
  'Datasheet',
  'ManufactureName',
  'TradeName',
  'Applications',
  'Reference',
  'versionOrRevisionNumber',
  'StorageTemperature',
  'Comments',
  'AvailabilityStatus',
  'driveFileUrl',
];

const MaterialsMainPage = (props) => {
  const {
    materialsData,
    isMaterialsLoading,
    materialsCount,
    onFetchMaterials,
    onBulkUploadMaterials,
    onShareMaterial,
    embeddedInModernLayout = false,
  } = props;

  const gridRef = useRef();
  const [rowData, setRowData] = useState([]);
  const [columnDefs, setColumnDefs] = useState([]);
  const [isPivotMode, setIsPivotMode] = useState(false);
  const [theme] = useState('ag-theme-material-dark');
  const [gridApi, setGridApi] = useState(null);
  const [materialsFormModal, setMaterialsFormModal] = useState(false);
  const [shareMaterialsModal, setShareMaterialsModal] = useState(false);
  const [emailAddress, setEmailAddress] = useState('');
  const [materialId, setMaterialId] = useState(0);
  const [certificateId, setCertificateId] = useState(null);
  const [editData, setEditData] = useState(null);
  const [materialRecord, setMaterialRecord] = useState({});
  const [isEdit, setIsEdit] = useState(false);
  const [materialForm] = Form.useForm();
  const [selectedRows, setSelectedRows] = useState([]);

  const [downloadLoading, setDownloadLoading] = useState(false);

  // Modern Layout State
  const [modernPageNumber, setModernPageNumber] = useState(1);
  const [modernPageSize, setModernPageSize] = useState(10);
  const [columnFilters, setColumnFilters] = useState({});
  const [filterRevisionDate, setFilterRevisionDate] = useState(null);
  const [filterFormerDate, setFilterFormerDate] = useState(null);
  const [sortField, setSortField] = useState(null);
  const [sortOrder, setSortOrder] = useState(null);

  const [bulkUploadModal, setBulkUploadModal] = useState(false);
  const [uploadFileList, setUploadFileList] = useState([]);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [disableFileUpload, setDisableFileUpload] = useState(false);
  const [pdfGenerationLoading, setPdfGenerationLoading] = useState(false);

  const searchCriteriaForFacets = useMemo(() => ({}), []);
  const rowDataRef = useRef([]);
  rowDataRef.current = Array.isArray(rowData) ? rowData : [];

  const fetchMaterialsFacetRows = useCallback(async () => {
    const list = rowDataRef.current;
    return Array.isArray(list) ? [...list] : [];
  }, []);

  const handleColumnFilterChange = useCallback((key, value) => {
    setColumnFilters((prev) => ({ ...prev, [key]: value }));
    setModernPageNumber(1);
  }, []);

  const { columnSelectedValues, applyFacetClientFilter, renderTextColumnFilterTrigger } =
    useHeatStyleColumnFacets({
      enabled: embeddedInModernLayout,
      filterableColumns: MATERIALS_TEXT_FACET_COLUMNS,
      listCount: Array.isArray(rowData) ? rowData.length : 0,
      projectList: rowData,
      searchCriteria: searchCriteriaForFacets,
      columnFilters,
      onColumnFilterChange: handleColumnFilterChange,
      fetchFacetRows: fetchMaterialsFacetRows,
    });

  // Column list mapping for Excel template
  const columnList = {
    ID: 'ID',
    MaterialCategory: 'Material Category',
    Name: 'Name',
    ProductCode: 'Product Code',
    Datasheet: 'Datasheet',
    ManufactureName: 'Manufacture Name',
    TradeName: 'Trade Name',
    Applications: 'Applications',
    Reference: 'Reference',
    RevisionDate: 'Revision Date',
    FormerDate: 'Former Date',
    versionOrRevisionNumber: 'Version / Revision Number',
    StorageTemperature: 'Storage Temperature',
    Comments: 'Comments',
    AvailabilityStatus: 'Availability Status',
    driveFileUrl: 'Drive File URL',
  };

  // Applications dropdown options
  const applicationOptions = [
    'Thermal Coating',
    'Thin Film Coating',
    'Anticorrossive',
    'Abrasive',
    'Additive',
  ];

  // Handler functions
  const onUpdateClick = useCallback((record) => {
    if (record != null) {
      setMaterialRecord(record);
      setIsEdit(true);
      setMaterialsFormModal(true);
    }
  }, []);

  const fetchMaterialsData = useCallback(() => {
    onFetchMaterials();
  }, [onFetchMaterials]);

  // Helper function to render PDF page as image using PDF.js
  // This reliably handles complex PDF structures by converting pages to images
  const renderPdfPageAsImage = async (pdfBytes, pageNumber) => {
    try {
      const loadingTask = pdfjsLib.getDocument({
        data: new Uint8Array(pdfBytes),
        useSystemFonts: true,
        verbosity: 0 // Suppress warnings
      });
      const pdf = await loadingTask.promise;
      const page = await pdf.getPage(pageNumber);

      // High-quality raster rendering for combined PDF output
      const viewport = page.getViewport({ scale: 2.5 });
      const canvas = document.createElement('canvas');
      const context = canvas.getContext('2d');
      canvas.height = viewport.height;
      canvas.width = viewport.width;

      // Render the page to canvas
      await page.render({
        canvasContext: context,
        viewport: viewport
      }).promise;

      // Convert canvas to data URL
      return canvas.toDataURL('image/png');
    } catch (error) {
      return null;
    }
  };

  // Helper function to get file extension from MIME type
  const getFileExtensionFromMimeType = useCallback((mimeType) => {
    const mimeToExtension = {
      'application/pdf': '.pdf',
      'application/msword': '.doc',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': '.docx',
      'application/vnd.ms-excel': '.xls',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': '.xlsx',
      'application/vnd.ms-powerpoint': '.ppt',
      'application/vnd.openxmlformats-officedocument.presentationml.presentation': '.pptx',
      'text/plain': '.txt',
      'text/csv': '.csv',
      'image/jpeg': '.jpg',
      'image/png': '.png',
      'image/gif': '.gif',
      'application/zip': '.zip',
      'application/x-zip-compressed': '.zip',
    };
    return mimeToExtension[mimeType] || '';
  }, []);

  // Handle download certificate
  const handleDownloadCertificate = useCallback(async (record) => {
    try {
      if (!record || !record.id) {
        notification.error({
          message: 'Invalid material record',
        });
        return;
      }

      // Get the latest certificate (highest version)
      const latestCertificate = record.latestCertificate || record.certificate;

      if (!latestCertificate || !latestCertificate.id) {
        notification.error({
          message: 'No certificate available',
          description: 'This material does not have a certificate to download.',
        });
        return;
      }

      // Call API to get presigned URL and file type, sending latest certificateId as query parameter
      const response = await axiosConfig.get(`/materials-module/${record.id}/download-certificate`, {
        params: {
          certificateId: latestCertificate.id,
        },
      });
      const presignedUrl = get(response, ['data', 'data', 'presignedUrl'], null) ||
        get(response, ['data', 'data', 'url'], null) ||
        get(response, ['data', 'presignedUrl'], null) ||
        get(response, ['data', 'url'], null);

      const fileType = get(response, ['data', 'data', 'fileType'], null) ||
        get(response, ['data', 'fileType'], null);

      // Get original filename from API response (prioritize this)
      const originalFileName = get(response, ['data', 'data', 'fileName'], null) ||
        get(response, ['data', 'data', 'file_name'], null) ||
        get(response, ['data', 'fileName'], null) ||
        get(response, ['data', 'file_name'], null);

      if (!presignedUrl) {
        notification.error({
          message: 'Failed to get download URL',
          description: 'The server did not return a valid download URL',
        });
        return;
      }

      // Open the presigned URL directly in a new tab to trigger the download.
      // Using window.open avoids CORS issues that occur when using fetch() against S3.
      const link = document.createElement('a');
      link.href = presignedUrl;
      link.target = '_blank';
      link.rel = 'noopener noreferrer';

      // If we have a filename, set the download attribute (browser may or may not honour it for cross-origin)
      const fileName = originalFileName || record.fileName || '';
      if (fileName) {
        link.download = fileName;
      }

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      notification.success({
        message: 'Download started',
      });
    } catch (error) {
      console.error('Error downloading certificate:', error);
      notification.error({
        message: 'Failed to download file',
        description: get(error, ['response', 'data', 'message'], error.message || 'An error occurred while downloading the file'),
      });
    }
  }, [getFileExtensionFromMimeType]);

  // Transform API response (camelCase) to table format (PascalCase)
  const transformMaterialsData = useCallback((data) => {
    if (!data || !Array.isArray(data)) return [];

    return data.map((item, index) => {
      // Handle certificates array - find the latest certificate (highest version)
      let latestCertificate = null;
      if (item.certificates && Array.isArray(item.certificates) && item.certificates.length > 0) {
        // Sort by fileVersion descending to get the latest (highest version)
        const sortedCertificates = [...item.certificates].sort((a, b) => {
          const versionA = a.fileVersion || 0;
          const versionB = b.fileVersion || 0;
          return versionB - versionA; // Descending order
        });
        latestCertificate = sortedCertificates[0];
      }

      return {
        id: item.id || index + 1,
        ID: item.id || index + 1,
        MaterialCategory: item.materialCategory || '',
        Name: item.name || '',
        ProductCode: item.productCode || '',
        Datasheet: item.datasheet || '',
        ManufactureName: item.manufactureName || '',
        TradeName: item.tradeName || '',
        Applications: item.applications || '',
        Reference: item.reference || '',
        RevisionDate: item.revisionDate || null,
        FormerDate: item.formerDate || null,
        versionOrRevisionNumber: item.versionOrRevisionNumber || '',
        StorageTemperature: item.storageTemperature || '',
        Comments: item.comments || '',
        AvailabilityStatus: item.availabilityStatus || '',
        driveFileUrl: item.driveFileUrl || '',
        certificates: item.certificates || [], // Preserve certificates array
        certificate: latestCertificate, // Latest certificate for backward compatibility
        latestCertificate: latestCertificate, // Explicit latest certificate reference
      };
    });
  }, []);

  const createExcelTemplate = async () => {
    const workbook = new ExcelJS.Workbook();
    const materialsSheet = workbook.addWorksheet('materials');

    // Set Header Row Height
    const headerRow = materialsSheet.getRow(1);
    headerRow.height = 20;

    // Exclude ID and Drive File URL columns from template
    const columns = Object.keys(columnList)
      .filter((key) => key !== 'ID' && key !== 'driveFileUrl')
      .map((key) => ({
        header: columnList[key],
        key: key,
        width: 20,
      }));

    // Set columns in the sheet
    materialsSheet.columns = columns;

    // Style the header row
    headerRow.eachCell((cell) => {
      cell.alignment = { vertical: 'middle', horizontal: 'center' };
      cell.font = { bold: true };
    });

    return await workbook.xlsx.writeBuffer();
  };

  const handleDownloadTemplate = async () => {
    try {
      const data = await createExcelTemplate();
      const blob = new Blob([data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'Materials Template.xlsx';
      link.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error creating template:', error);
    }
  };

  const handleDownloadData = async () => {
    const columns = Object.keys(columnList)
      .filter((key) => key !== 'ID' && key !== 'driveFileUrl')
      .map((key) => ({
        header: columnList[key],
        key: key,
        width: 20,
      }));

    const dataFormatter = (record) => {
      return {
        ...record,
        RevisionDate: record.RevisionDate
          ? moment.utc(record.RevisionDate).format('DD-MMM-YYYY')
          : '',
      };
    };

    await downloadDataAsExcel({
      data: rowData,
      fileName: 'List of Materials',
      columns,
      sheetName: 'materials',
      dataFormatter,
      reverseData: true,
      setDownloadLoading,
    });
  };

  const showShareMaterialsModal = useCallback(async (record) => {
    // Get the latest certificate (highest version)
    const latestCertificate = record.latestCertificate || record.certificate;
    setMaterialId(record.id);
    setCertificateId(latestCertificate?.id || null);
    setShareMaterialsModal(true);
  }, []);

  const showBulkUploadModal = () => {
    setBulkUploadModal(true);
  };

  const handleCancelBulkUploadModal = () => {
    setBulkUploadModal(false);
    setUploadFileList([]);
    setDisableFileUpload(false);
  };

  // Excel date conversion helper
  const ExcelDateToJSDate = (date) => {
    const convertedDate = new Date(Math.round((date - 25569) * 864e5));
    return String(convertedDate).slice(4, 15);
  };

  const handleBulkUpload = async () => {
    if (uploadFileList.length === 0) {
      notification.warn({ message: 'Please select an Excel file to upload.' });
      return;
    }

    const excelFile = uploadFileList[0];

    if (!excelFile || !excelFile.name.match(/\.(xlsx|xls)$/i)) {
      notification.warn({ message: 'Please select a valid Excel file (.xlsx or .xls).' });
      return;
    }

    const reader = new FileReader();
    reader.onload = async (event) => {
      try {
        const data = new Uint8Array(event.target.result);
        const workbook = XLSX.read(data, { type: 'array' });
        const sheet = workbook.Sheets[workbook.SheetNames[0]];
        const jsonData = XLSX.utils.sheet_to_json(sheet, { header: 1 });

        if (jsonData.length <= 1) {
          notification.error({ message: 'Invalid Excel content. Please ensure the file has data rows.' });
          return;
        }

        // Map Excel headers (display names) to API field names (camelCase)
        const keyMapping = {
          'ID': 'id',
          'Material Category': 'materialCategory',
          'Name': 'name',
          'Product Code': 'productCode',
          'Datasheet': 'datasheet',
          'Manufacture Name': 'manufactureName',
          'Trade Name': 'tradeName',
          'Applications': 'applications',
          'Reference': 'reference',
          'Revision Date': 'revisionDate',
          'Former Date': 'formerDate',
          'Version / Revision Number': 'versionOrRevisionNumber',
          'Storage Temperature': 'storageTemperature',
          'Comments': 'comments',
          'Availability Status': 'availabilityStatus',
          'Drive File URL': 'driveFileUrl',
        };

        const swappedKeyMapping = Object.fromEntries(
          Object.entries(keyMapping).map(([k, v]) => [v, k])
        );

        const headers = jsonData[0];
        const formattedData = jsonData.slice(1).map((row, index) => {
          const rowData = {};
          headers.forEach((header, i) => {
            const mappedKey = keyMapping[header];
            const value = row[i];

            if (!mappedKey) {
              return; // Skip unmapped columns
            }

            // Handle date fields (Revision Date, Former Date)
            if (header === 'Revision Date' || header === 'Former Date') {
              let formattedDate = null;

              if (value) {
                // Try parsing common date formats
                formattedDate = moment(value, [
                  'DD-MMM-YYYY',
                  'DD-MM-YYYY',
                  'D-M-YY',
                  'DD-MM-YY',
                  'MM-DD-YYYY',
                  'YYYY-MM-DD',
                ]).format('YYYY-MM-DD');

                // If parsing failed, try Excel date conversion
                if (!formattedDate || formattedDate.includes('Invalid') || !moment(formattedDate).isValid()) {
                  try {
                    const excelDate = ExcelDateToJSDate(value);
                    formattedDate = moment(excelDate).format('YYYY-MM-DD');
                  } catch (e) {
                    formattedDate = null;
                  }
                }

                // Convert to ISO string format for API
                if (formattedDate && moment(formattedDate).isValid()) {
                  rowData[mappedKey] = formattedDate + 'T00:00:00.000Z';
                } else {
                  rowData[mappedKey] = null;
                }
              } else {
                rowData[mappedKey] = null;
              }
            } else {
              // Normal fields (including driveFileUrl) - use value as is or empty string
              rowData[mappedKey] = value !== undefined && value !== null ? String(value).trim() : '';
            }
          });

          return rowData;
        });

        // Filter out empty rows
        const validData = formattedData.filter((row) => {
          return row.name || row.materialCategory || row.manufactureName;
        });

        if (validData.length === 0) {
          notification.error({ message: 'No valid data found in the Excel file.' });
          return;
        }

        setUploadLoading(true);
        await onBulkUploadMaterials({ data: validData });
        await onFetchMaterials();
        setUploadLoading(false);
        setBulkUploadModal(false);
        setUploadFileList([]);
        setDisableFileUpload(false);
      } catch (error) {
        console.error('Error parsing Excel:', error);
        notification.error({ message: 'Error parsing Excel file. Please check the file format.' });
        setUploadLoading(false);
      }
    };

    reader.readAsArrayBuffer(excelFile);
  };

  const handleMaterialsFileInputChange = (event) => {
    const input = event?.target;
    const file = input?.files?.[0];
    if (!file) return;
    setUploadFileList([file]);
    setDisableFileUpload(true);
    if (input) input.value = '';
  };

  const clearSelectedMaterialsFile = () => {
    setUploadFileList([]);
    setDisableFileUpload(false);
  };

  const renderMaterialsBulkUploadPicker = () => (
    <div className="uploadFile_admin calibration-bulk-upload-custom">
      <p className="calibration-bulk-upload-custom__intro">
        Select an Excel (.xlsx or .xls) file and then press Upload.
      </p>
      <div className="calibration-bulk-upload-custom__zone">
        <input
          type="file"
          accept={MATERIALS_BULK_UPLOAD_ACCEPT}
          className="calibration-bulk-upload-custom__input"
          onChange={handleMaterialsFileInputChange}
          disabled={uploadLoading || disableFileUpload}
        />
        <div className="calibration-bulk-upload-custom__face" aria-hidden="true">
          <CloudUploadOutlined className="calibration-bulk-upload-custom__icon" />
          <span className="calibration-bulk-upload-custom__title">
            Tap here or anywhere in the box to browse
          </span>
          <span className="calibration-bulk-upload-custom__sub">Excel (.xlsx, .xls) supported</span>
        </div>
      </div>
      {uploadFileList.length > 0 && (
        <>
          <div className="calibration-bulk-upload-custom__summary" role="status">
            1 file selected
          </div>
          <div className="calibration-bulk-upload-custom__files">
            <div className="calibration-bulk-upload-custom__file-chip">
              <span className="calibration-bulk-upload-custom__file-name" title={uploadFileList[0].name}>
                {uploadFileList[0].name}
              </span>
              <button
                type="button"
                className="calibration-bulk-upload-custom__file-remove"
                onClick={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  clearSelectedMaterialsFile();
                }}
                aria-label={`Remove ${uploadFileList[0].name}`}
              >
                <CloseOutlined />
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );

  const handleCancelShareMaterialsModal = () => {
    setShareMaterialsModal(false);
    setEmailAddress('');
    setCertificateId(null);
  };

  const shareMaterials = async () => {
    if (!emailAddress || !emailAddress.trim()) {
      notification.warning({
        message: 'Please enter email address(es)',
      });
      return;
    }

    const emailAddressArray = emailAddress.split(',').map((email) => email.trim()).filter((email) => email);

    if (emailAddressArray.length === 0) {
      notification.warning({
        message: 'Please enter valid email address(es)',
      });
      return;
    }

    await onShareMaterial({ materialId, certificateId, emailAddress: emailAddressArray });
    setShareMaterialsModal(false);
    setEmailAddress('');
    setCertificateId(null);
  };

  // Handle Generate Combined PDF Report
  const handleGeneratePdf = async () => {
    // Set loading state and disable button
    setPdfGenerationLoading(true);

    try {
      // Filter selected rows to only include those with certificates
      let rowsWithCertificates = selectedRows.filter((row) => {
        const hasCertificates = row?.certificates && Array.isArray(row.certificates) && row.certificates.length > 0;
        return hasCertificates || (row?.certificate && row.certificate !== null);
      });

      if (rowsWithCertificates.length === 0) {
        notification.warning({
          message: 'Please select at least one material with a certificate to generate the report',
        });
        setPdfGenerationLoading(false);
        return;
      }

      // Sort by Datasheet in ascending order for combined report
      rowsWithCertificates = [...rowsWithCertificates].sort((a, b) => {
        const datasheetA = (a.Datasheet || '').toLowerCase();
        const datasheetB = (b.Datasheet || '').toLowerCase();
        if (datasheetA < datasheetB) return -1;
        if (datasheetA > datasheetB) return 1;
        return 0;
      });

      // Extract material IDs from selected rows that have certificates
      const materialIds = rowsWithCertificates.map((row) => row.id).filter((id) => id);

      if (materialIds.length === 0) {
        notification.warning({
          message: 'Please select at least one material to generate the report',
        });
        setPdfGenerationLoading(false);
        return;
      }

      // Call API to get presigned URLs
      const response = await axiosConfig.post('/materials-module/bulk-certificate-presigned-url', {
        materialIds: materialIds,
      });

      const presignedUrlsData = get(response, ['data', 'data'], []) || get(response, ['data'], []);

      if (!presignedUrlsData || !Array.isArray(presignedUrlsData) || presignedUrlsData.length === 0) {
        notification.error({
          message: 'Failed to get presigned URLs',
          description: 'The server did not return valid presigned URLs',
        });
        setPdfGenerationLoading(false);
        return;
      }

      // Create a map of materialsModuleId to presignedUrl for easy lookup
      const materialIdToUrlMap = {};
      presignedUrlsData.forEach((item) => {
        const materialsModuleId = item.materialsModuleId;
        const presignedUrl = item.presignedUrl;
        if (materialsModuleId && presignedUrl) {
          materialIdToUrlMap[materialsModuleId] = presignedUrl;
        }
      });

      // Get certificate URL function for Materials
      const getCertificateUrl = async (row) => {
        const materialId = row?.id;
        if (!materialId) {
          return null;
        }
        return materialIdToUrlMap[materialId] || null;
      };

      // Table configuration for Materials
      const getColumnWidths = (font, tableWidth, padding, fontSize) => {
        const seqWidth = font.widthOfTextAtSize('S/N', fontSize) + padding;
        const materialNameWidth = font.widthOfTextAtSize('Material Name', fontSize) + padding;
        const manufacturerNameWidth = font.widthOfTextAtSize('Manufacturer Name', fontSize) + padding;
        const datasheetWidth = font.widthOfTextAtSize('Datasheet', fontSize) + padding;

        // Calculate remaining width for Material Name (largest column)
        const totalFixedWidth = seqWidth + manufacturerNameWidth + datasheetWidth;
        const materialNameAdjustedWidth = Math.max(materialNameWidth, tableWidth - totalFixedWidth);

        return {
          seq: seqWidth,
          materialName: materialNameAdjustedWidth,
          manufacturerName: manufacturerNameWidth,
          datasheet: datasheetWidth,
        };
      };

      const formatRowData = (row, index) => {
        return [
          (index + 1).toString(),
          row?.Name || '-',
          row?.ManufactureName || '-',
          row?.Datasheet || '-',
        ];
      };

      // Generate PDF using common utility
      const result = await generateCombinedPdfReport({
        selectedRows: rowsWithCertificates,
        renderPdfPageAsImage,
        getCertificateUrl,
        reportConfig: {
          heading: 'Material Certificates',
          section1Title: '1.0 Material',
          section2Title: '2.0 Material Certificates',
          fileName: 'Combined Material Report.pdf',
        },
        tableConfig: {
          headers: ['S/N', 'Material Name', 'Manufacturer Name', 'Datasheet'],
          getColumnWidths,
          formatRowData,
          styling: {
            rowHeight: 35,
            fontSize: 9,
            headerFontSize: 10,
            enableTextWrapping: true,
            wrapText,
          },
        },
      });

      // Show notification to user
      if (result.successCount > 0) {
        notification.success({
          message: 'PDF Generated Successfully',
          description: `Merged ${result.successCount} certificate(s) with ${result.totalPages} total page(s).`,
          duration: 5,
        });
      }
      if (result.failCount > 0) {
        notification.warning({
          message: 'Some PDFs Failed to Merge',
          description: `${result.failCount} certificate(s) could not be merged.`,
          duration: 5,
        });
      }
    } catch (error) {
      console.error('Error generating PDF:', error);
      notification.error({
        message: 'Error Generating PDF',
        description: get(error, ['response', 'data', 'message'], error.message || 'An error occurred while generating the PDF.'),
        duration: 5,
      });
    } finally {
      // Always reset loading state when done (success or error)
      setPdfGenerationLoading(false);
    }
  };

  // Define column definitions
  useEffect(() => {
    const columns = [
      {
        headerName: 'S/N',
        width: 30,
        maxWidth: 120,
        checkboxSelection: true,
        headerCheckboxSelection: true,
        headerCheckboxSelectionFilteredOnly: true,
        cellStyle: { textAlign: 'left' },
        valueGetter: (params) => {
          return params.node.rowIndex + 1;
        },
        filter: 'agNumberColumnFilter',
        sortable: true,
        comparator: (valueA, valueB) => {
          return valueA - valueB;
        },
      },
      {
        field: 'MaterialCategory',
        headerName: 'Material Category',
        minWidth: 180,
        filter: 'agMultiColumnFilter',
        filterParams: {
          filters: [
            { filter: 'agTextColumnFilter' },
            { filter: 'agSetColumnFilter' },
          ],
        },
        cellStyle: { textAlign: 'left' },
      },
      {
        field: 'Name',
        headerName: 'Name',
        minWidth: 200,
        filter: 'agMultiColumnFilter',
        filterParams: {
          filters: [
            { filter: 'agTextColumnFilter' },
            { filter: 'agSetColumnFilter' },
          ],
        },
        cellStyle: { textAlign: 'left' },
      },
      {
        field: 'ProductCode',
        headerName: 'Product Code',
        minWidth: 150,
        filter: 'agMultiColumnFilter',
        filterParams: {
          filters: [
            { filter: 'agTextColumnFilter' },
            { filter: 'agSetColumnFilter' },
          ],
        },
        cellStyle: { textAlign: 'left' },
      },
      {
        field: 'Datasheet',
        headerName: 'Datasheet',
        minWidth: 150,
        sort: 'asc',
        filter: 'agMultiColumnFilter',
        filterParams: {
          filters: [
            { filter: 'agTextColumnFilter' },
            { filter: 'agSetColumnFilter' },
          ],
        },
        cellStyle: { textAlign: 'left' },
      },
      {
        field: 'ManufactureName',
        headerName: 'Manufacture Name',
        minWidth: 200,
        filter: 'agMultiColumnFilter',
        filterParams: {
          filters: [
            { filter: 'agTextColumnFilter' },
            { filter: 'agSetColumnFilter' },
          ],
        },
        cellStyle: { textAlign: 'left' },
      },
      {
        field: 'TradeName',
        headerName: 'Trade Name',
        minWidth: 150,
        filter: 'agMultiColumnFilter',
        filterParams: {
          filters: [
            { filter: 'agTextColumnFilter' },
            { filter: 'agSetColumnFilter' },
          ],
        },
        cellStyle: { textAlign: 'left' },
      },
      {
        field: 'Applications',
        headerName: 'Applications',
        minWidth: 180,
        cellEditor: 'agSelectCellEditor',
        cellEditorParams: {
          values: applicationOptions,
        },
        editable: true,
        filter: 'agSetColumnFilter',
        filterParams: {
          values: applicationOptions,
        },
        cellStyle: { textAlign: 'center' },
      },
      {
        field: 'Reference',
        headerName: 'Reference',
        minWidth: 150,
        filter: 'agMultiColumnFilter',
        filterParams: {
          filters: [
            { filter: 'agTextColumnFilter' },
            { filter: 'agSetColumnFilter' },
          ],
        },
        cellStyle: { textAlign: 'left' },
      },
      {
        field: 'RevisionDate',
        headerName: 'Revision Date',
        width: 200,
        minWidth: 200,
        resizable: true,
        filter: 'agDateColumnFilter',
        filterParams: {
          filterOptions: ['equals', 'greaterThan', 'lessThan', 'inRange'],
          buttons: ['reset'],
          comparator: (filterLocalDateAtMidnight, cellValue) => {
            if (!cellValue) return -1;
            const cellDate = moment.utc(cellValue).startOf('day');
            const filterDate = moment(filterLocalDateAtMidnight).startOf('day');
            if (cellDate.isSame(filterDate)) return 0;
            if (cellDate.isBefore(filterDate)) return -1;
            return 1;
          },
          browserDatePicker: true,
        },
        valueGetter: (params) => {
          if (!params.data || !params.data.RevisionDate) return null;
          const date = moment.utc(params.data.RevisionDate);
          return date.isValid() ? date.toDate() : null;
        },
        valueParser: (params) => {
          if (!params.newValue) return null;
          const date = moment(params.newValue);
          return date.isValid() ? date.toDate() : null;
        },
        cellRenderer: (params) => {
          if (!params.value) return '';
          const date = moment.utc(params.value);
          return date.isValid() ? date.format('DD-MMM-YYYY') : params.value;
        },
        cellStyle: { textAlign: 'center' },
        headerClass: 'ag-center-header',
      },
      {
        field: 'FormerDate',
        headerName: 'Former Date',
        width: 200,
        minWidth: 200,
        resizable: true,
        filter: 'agDateColumnFilter',
        filterParams: {
          filterOptions: ['equals', 'greaterThan', 'lessThan', 'inRange'],
          buttons: ['reset'],
          comparator: (filterLocalDateAtMidnight, cellValue) => {
            if (!cellValue) return -1;
            const cellDate = moment.utc(cellValue).startOf('day');
            const filterDate = moment(filterLocalDateAtMidnight).startOf('day');
            if (cellDate.isSame(filterDate)) return 0;
            if (cellDate.isBefore(filterDate)) return -1;
            return 1;
          },
          browserDatePicker: true,
        },
        valueGetter: (params) => {
          if (!params.data || !params.data.FormerDate) return null;
          const date = moment.utc(params.data.FormerDate);
          return date.isValid() ? date.toDate() : null;
        },
        valueParser: (params) => {
          if (!params.newValue) return null;
          const date = moment(params.newValue);
          return date.isValid() ? date.toDate() : null;
        },
        cellRenderer: (params) => {
          if (!params.value) return '';
          const date = moment.utc(params.value);
          return date.isValid() ? date.format('DD-MMM-YYYY') : params.value;
        },
        cellStyle: { textAlign: 'center' },
        headerClass: 'ag-center-header',
      },
      {
        field: 'versionOrRevisionNumber',
        headerName: 'Version / Revision Number',
        minWidth: 180,
        filter: 'agMultiColumnFilter',
        filterParams: {
          filters: [
            { filter: 'agTextColumnFilter' },
            { filter: 'agSetColumnFilter' },
          ],
        },
        cellStyle: { textAlign: 'left' },
      },
      {
        field: 'StorageTemperature',
        headerName: 'Storage Temperature (°C)',
        minWidth: 180,
        filter: 'agMultiColumnFilter',
        filterParams: {
          filters: [
            { filter: 'agTextColumnFilter' },
            { filter: 'agSetColumnFilter' },
          ],
        },
        cellStyle: { textAlign: 'center' },
      },
      {
        field: 'Comments',
        headerName: 'Comments',
        minWidth: 300,
        flex: 1,
        filter: 'agTextColumnFilter',
        cellStyle: { textAlign: 'left' },
      },
      {
        field: 'AvailabilityStatus',
        headerName: 'Availability Status',
        minWidth: 150,
        filter: 'agSetColumnFilter',
        filterParams: {
          values: ['Available', 'Restricted Supply', 'Discontinued'],
        },
        cellStyle: { textAlign: 'center' },
      }
    ];

    // Add the actions column at the end
    columns.push({
      headerName: 'Actions',
      field: 'action',
      pinned: 'right',
      width: 200,
      cellRenderer: (params) => {
        const record = params.data;
        // Check if certificates array exists and is not empty
        const hasCertificate = (record?.certificates && Array.isArray(record.certificates) && record.certificates.length > 0) ||
          (record?.certificate && record.certificate !== null);

        return (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
            <Tooltip title="Update">
              <Button
                className="p-0 border-0"
                size="small"
                target="_blank"
                style={{ marginRight: '10px' }}
              >
                <EditOutlined className="pdfIconRed" onClick={() => onUpdateClick(record)} />
              </Button>
            </Tooltip>
            {hasCertificate && (
              <>
                <Tooltip title="Download">
                  <Button
                    className="p-0 border-0"
                    size="small"
                    style={{ marginRight: '10px' }}
                    onClick={() => handleDownloadCertificate(record)}
                  >
                    <FileExcelOutlined className="pdfIconRed" />
                  </Button>
                </Tooltip>
                <Tooltip title="Share Material">
                  <Button
                    className="p-0 border-0"
                    size="small"
                    style={{ marginRight: '10px' }}
                    onClick={() => showShareMaterialsModal(record)}
                  >
                    <ShareAltOutlined className="pdfIconRed" style={{ marginRight: '00px' }} />
                  </Button>
                </Tooltip>
              </>
            )}
          </div>
        );
      },
      cellStyle: { textAlign: 'center' },
      filter: false,
    });

    setColumnDefs(columns);
  }, [onUpdateClick, showShareMaterialsModal, handleDownloadCertificate]);

  // Fetch materials data on component mount
  useEffect(() => {
    onFetchMaterials();
  }, [onFetchMaterials]);

  // Update rowData when materialsData changes
  useEffect(() => {
    if (materialsData && materialsData.length > 0) {
      const transformedData = transformMaterialsData(materialsData);
      // Sort by Datasheet in ascending order
      const sortedData = [...transformedData].sort((a, b) => {
        const datasheetA = (a.Datasheet || '').toLowerCase();
        const datasheetB = (b.Datasheet || '').toLowerCase();
        if (datasheetA < datasheetB) return -1;
        if (datasheetA > datasheetB) return 1;
        return 0;
      });
      setRowData(sortedData);
    } else {
      setRowData([]);
    }
  }, [materialsData, transformMaterialsData]);

  const onRowDragEnd = (event) => {
    const { node, overNode } = event;
    if (!overNode) return;

    const draggedRow = node.data;
    const fromIndex = node.rowIndex;
    const toIndex = overNode.rowIndex;

    if (fromIndex === toIndex) return;

    const newRowData = [...rowData];
    newRowData.splice(fromIndex, 1);
    newRowData.splice(toIndex, 0, draggedRow);

    setRowData(newRowData);
  };

  //   const autoGroupColumnDef = useMemo(() => {
  //     return {
  //       minWidth: 200,
  //       pinned: 'left',
  //     };
  //   }, []);


  const statusBar = useMemo(() => {
    return {
      statusPanels: [
        {
          statusPanel: 'agTotalRowCountComponent',
          align: 'left',
        },
        {
          statusPanel: 'agSelectedRowCountComponent',
          align: 'left',
        },
        {
          statusPanel: 'agFilteredRowCountComponent',
          align: 'left',
        },
      ],
    };
  }, []);

  //   const activeKeyLine = 'Materials';
  //   const defaultExcelExportParams = useMemo(() => {
  //     return {
  //       fileName: `${activeKeyLine}.xlsx`, // Default file name
  //       allColumns: true, // Include all columns by default
  //       onlySelected: false, // Include all rows, not just selected ones
  //       sheetName: 'Sheet1', // Default sheet name
  //     };
  //   }, [activeKeyLine]);

  const gridStyle = useMemo(() => ({ height: '99%', width: '100%' }), []);

  const defaultColDef = useMemo(() => {
    return {
      flex: 1,
      minWidth: 230,
      floatingFilter: true,
      resizable: true,
      enableValue: true,
      enableRowGroup: true,
      enablePivot: true,
      filter: true,
    };
  }, []);

  const onGridReady = useCallback(
    (params) => {
      setGridApi(params.api);
      if (isMaterialsLoading) {
        params.api.showLoadingOverlay();
      } else {
        params.api.hideOverlay();
      }
    },
    [isMaterialsLoading]
  );

  // Show/hide loading overlay when loading state changes
  useEffect(() => {
    if (gridApi) {
      if (isMaterialsLoading) {
        gridApi.showLoadingOverlay();
      } else {
        gridApi.hideOverlay();
      }
    }
  }, [isMaterialsLoading, gridApi]);

  const getPipelineTabpanes = () => {
    return (
      <div className="custom-tab h-100" style={{ paddingTop: '0px' }}>
        <div style={{ height: 'calc(100vh - 120px)', overflow: 'auto', position: 'relative', width: '100%' }}>
          <div className={`ag-grid ${theme} custom-grid-theme`} style={{ ...gridStyle, width: '100%', height: '100%' }}>
            <AgGridReact
              ref={gridRef}
              rowData={rowData}
              columnDefs={columnDefs}
              rowDragManaged={true}
              onRowDragEnd={onRowDragEnd}
              defaultColDef={defaultColDef}
              onGridReady={onGridReady}
              sideBar={false}
              pivotMode={isPivotMode}
              animateRows={true}
              rowHeight={40}
              headerHeight={50}
              rowSelection="multiple"
              suppressHorizontalScroll={false}
              domLayout="normal"
              statusBar={statusBar}
              onSelectionChanged={(event) => {
                const selected = event.api.getSelectedRows();
                setSelectedRows(selected);
                console.log('Selected rows:', selected);
              }}
            />
          </div>
        </div>
      </div>
    );
  };

  const handleSortClick = (columnKey) => {
    setSortField(columnKey);
    setSortOrder((prev) => (sortField === columnKey && prev === 'ascend' ? 'descend' : 'ascend'));
    setModernPageNumber(1);
  };

  const filteredDataForModern = useMemo(() => {
    if (!embeddedInModernLayout || !rowData || !rowData.length) return [];
    const textDateFiltered = rowData.filter((row) => {
      for (const key of MATERIALS_TEXT_FACET_COLUMNS) {
        const sel = columnSelectedValues[key];
        if (sel != null && sel.size > 0) continue;
        const val = (columnFilters[key] || '').trim();
        if (!val) continue;
        const cell = row[key];
        const str = cell != null ? String(cell) : '';
        if (!str.toLowerCase().includes(val.toLowerCase())) return false;
      }

      if (filterRevisionDate && row.RevisionDate) {
        if (!moment(row.RevisionDate).isSame(filterRevisionDate, 'day')) return false;
      }
      if (filterFormerDate && row.FormerDate) {
        if (!moment(row.FormerDate).isSame(filterFormerDate, 'day')) return false;
      }

      return true;
    });
    return applyFacetClientFilter(textDateFiltered);
  }, [
    embeddedInModernLayout,
    rowData,
    columnFilters,
    columnSelectedValues,
    filterRevisionDate,
    filterFormerDate,
    applyFacetClientFilter,
  ]);

  const sortedDataForModern = useMemo(() => {
    if (!sortField || !sortOrder || !filteredDataForModern.length) return filteredDataForModern;
    const arr = [...filteredDataForModern];
    const isDate = MATERIALS_DATE_COLUMN_KEYS.includes(sortField);
    const dir = sortOrder === 'ascend' ? 1 : -1;
    arr.sort((a, b) => {
      let va = a[sortField];
      let vb = b[sortField];
      if (isDate) {
        const ma = va ? moment(va).valueOf() : 0;
        const mb = vb ? moment(vb).valueOf() : 0;
        return dir * (ma - mb);
      }
      const sa = va != null ? String(va) : '';
      const sb = vb != null ? String(vb) : '';
      return dir * (sa.localeCompare(sb, undefined, { numeric: true }));
    });
    return arr;
  }, [filteredDataForModern, sortField, sortOrder]);

  const ExpandableText = ({ text, limit = 50 }) => {
    const [expanded, setExpanded] = useState(false);

    if (!text || text.length <= limit) return text;

    return (
      <span>
        {expanded ? text : `${text.substring(0, limit)}...`}
        <span
          style={{ color: '#1890ff', cursor: 'pointer', marginLeft: '4px', whiteSpace: 'nowrap' }}
          onClick={(e) => {
            e.stopPropagation();
            setExpanded(!expanded);
          }}
        >
          {expanded ? ' Read less' : ' Read more'}
        </span>
      </span>
    );
  };

  const modernColumnsWithFilters = useMemo(() => {
    const keys = Object.keys(columnList).filter((key) => key !== 'ID'); // Exclude ID from display if not needed
    const cols = keys.map((key) => {
      const isDate = MATERIALS_DATE_COLUMN_KEYS.includes(key);
      const label = columnList[key];
      let title = label;

      if (isDate) {
        const dateVal = key === 'RevisionDate' ? filterRevisionDate : filterFormerDate;
        const setDate = key === 'RevisionDate' ? setFilterRevisionDate : setFilterFormerDate;
        title = (
          <div className="column-header-with-filter">
            <div className="column-header-text-wrapper">
              <span>{label}</span>
              <span
                className="custom-sort-icon"
                role="button"
                tabIndex={0}
                onClick={(e) => { e.stopPropagation(); e.preventDefault(); handleSortClick(key); }}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleSortClick(key); } }}
              >
                {sortField === key && sortOrder === 'ascend' && <CaretUpOutlined />}
                {sortField === key && sortOrder === 'descend' && <CaretDownOutlined />}
                {(!sortField || sortField !== key || !sortOrder) && (
                  <span className="sort-icon-placeholder"><CaretUpOutlined /></span>
                )}
              </span>
            </div>
            <div className="header-filter-input">
              <DatePicker
                className="heat-profile-header-date-picker"
                placeholder="Select date"
                size="small"
                style={{ width: '120px' }}
                format="DD-MMM-YYYY"
                value={dateVal}
                onChange={(d) => { setDate(d); setModernPageNumber(1); }}
                onClick={(e) => e.stopPropagation()}
                suffixIcon={dateVal ? (
                  <CloseOutlined className="heat-profile-date-clear-icon" onClick={(e) => { e.stopPropagation(); setDate(null); }} />
                ) : (
                  <CalendarOutlined className="heat-profile-date-icon" />
                )}
                dropdownClassName="heat-profile-datepicker-dropdown"
                allowClear={false}
              />
            </div>
          </div>
        );
      } else {
        title = (
          <div className="column-header-with-filter">
            <div className="column-header-text-wrapper">
              <span>{label}</span>
              <span
                className="custom-sort-icon"
                role="button"
                tabIndex={0}
                onClick={(e) => { e.stopPropagation(); e.preventDefault(); handleSortClick(key); }}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleSortClick(key); } }}
              >
                {sortField === key && sortOrder === 'ascend' && <CaretUpOutlined />}
                {sortField === key && sortOrder === 'descend' && <CaretDownOutlined />}
                {(!sortField || sortField !== key || !sortOrder) && (
                  <span className="sort-icon-placeholder"><CaretUpOutlined /></span>
                )}
              </span>
              {MATERIALS_TEXT_FACET_COLUMNS.includes(key) ? renderTextColumnFilterTrigger(key) : null}
            </div>
            <div className="header-filter-input">
              <div className="heat-profile-inline-field-shell heat-profile-inline-field-shell--sm heat-profile-header-inline-shell">
                <Input
                  placeholder="Search"
                  size="small"
                  style={{ width: '120px' }}
                  value={columnFilters[key] || ''}
                  onChange={(e) => handleColumnFilterChange(key, e.target.value)}
                  onClick={(e) => e.stopPropagation()}
                />
              </div>
            </div>
          </div>
        );
      }

      const calculatedWidth = Math.max(label.length * 10 + 60, 150);
      const isLongTextColumn = ['Comments', 'Applications', 'Reference'].includes(key);

      return {
        title,
        dataIndex: key,
        key,
        align: 'left',
        width: isDate ? 220 : (isLongTextColumn ? 300 : calculatedWidth),
        minWidth: 150,
        sorter: true,
        sortOrder: sortField === key ? sortOrder : undefined,
        render: (val) => {
          if (isDate) return val ? moment(val).format('DD-MMM-YYYY') : '-';
          const textVal = val === undefined || val === null || val === '' ? '-' : String(val);

          if (isLongTextColumn || textVal.length > 50) {
            return <ExpandableText text={textVal} limit={50} />;
          }
          return textVal;
        },
      };
    });

    // Actions Column
    cols.push({
      title: 'ACTIONS',
      key: 'action',
      align: 'center',
      width: 160,
      render: (_, record) => {
        // Find latest certificate for download/share
        const latestCert = record.latestCertificate || record.certificate;
        const hasCert = latestCert && latestCert.id;

        return (
          <div className="heat-profile-actions-cell">
            <Tooltip title="Update">
              <EditOutlined className="txtIcon" onClick={() => onUpdateClick(record)} />
            </Tooltip>
            {hasCert && (
              <Tooltip title="Download Certificate">
                <span onClick={() => handleDownloadCertificate(record)} className="txtIcon" style={{ cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                  <FileExcelOutlined />
                </span>
              </Tooltip>
            )}
            <Tooltip title="Share Material">
              <ShareAltOutlined className="txtIcon" onClick={() => showShareMaterialsModal(record)} />
            </Tooltip>
          </div>
        );
      },
    });

    return cols;
  }, [
    columnFilters,
    filterRevisionDate,
    filterFormerDate,
    sortField,
    sortOrder,
    embeddedInModernLayout,
    onUpdateClick,
    showShareMaterialsModal,
    handleDownloadCertificate,
    handleColumnFilterChange,
    handleSortClick,
    renderTextColumnFilterTrigger,
  ]);

  const totalMaterialsCount = sortedDataForModern.length;

  const tablePaginationModern = totalMaterialsCount > 0 ? {
    position: ['bottomRight'],
    className: 'heat-profile-pagination materials-pagination',
    showSizeChanger: true,
    total: totalMaterialsCount,
    current: modernPageNumber,
    pageSize: modernPageSize,
    pageSizeOptions: ['10', '20', '50', String(totalMaterialsCount)],
    showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
    locale: { items_per_page: '/ page' },
    onChange: (pageNumber, pageSize) => {
      setModernPageNumber(pageNumber);
      setModernPageSize(pageSize || 10);
    },
  } : false;

  const paginatedDataForModern = useMemo(() => {
    if (modernPageSize >= sortedDataForModern.length) return sortedDataForModern;
    const start = (modernPageNumber - 1) * modernPageSize;
    return sortedDataForModern.slice(start, start + modernPageSize);
  }, [sortedDataForModern, modernPageNumber, modernPageSize]);

  // Replace the numeric "All" option label (e.g. "42 / page") with "All / page"
  useEffect(() => {
    const totalStr = String(totalMaterialsCount);
    const replaceLabels = () => {
      document.querySelectorAll('.materials-pagination .ant-select-selection-item').forEach((el) => {
        if (el.textContent?.includes(totalStr) && el.textContent?.includes('/ page')) {
          el.textContent = 'All / page';
          el.title = 'All / page';
        }
      });
      document.querySelectorAll('.ant-select-item-option-content').forEach((el) => {
        if (el.textContent?.includes(totalStr) && el.textContent?.includes('/ page')) {
          el.textContent = 'All / page';
        }
      });
    };
    replaceLabels();
    const wrapper = document.querySelector('.materials-pagination');
    const observer = wrapper ? new MutationObserver(replaceLabels) : null;
    if (observer && wrapper) observer.observe(wrapper, { childList: true, subtree: true, characterData: true });
    const handleClick = () => { setTimeout(replaceLabels, 50); setTimeout(replaceLabels, 150); };
    wrapper?.addEventListener('mousedown', handleClick);
    return () => { observer?.disconnect(); wrapper?.removeEventListener('mousedown', handleClick); };
  }, [totalMaterialsCount, modernPageSize]);

  if (embeddedInModernLayout) {
    return (
      <div className="heat-profile-card">
        <div className="heat-profile-list-header">
          <div className="heat-profile-header-left">
            <div className="heat-profile-icon-wrap">
              <ToolOutlined style={{ fontSize: 22 }} />
            </div>
            <div className="heat-profile-header-content">
              <div className="heat-profile-title-row">
                <h3 className="heat-profile-list-title">List of Materials</h3>
                <span className="heat-profile-summary">
                  {materialsCount != null ? materialsCount : (rowData?.length ?? 0)} item{((materialsCount != null ? materialsCount : rowData?.length) ?? 0) !== 1 ? 's' : ''}
                </span>
              </div>
              <p className="heat-profile-list-description">
                Manage materials, datasheets, and certificates.
              </p>
            </div>
          </div>
          <div className="heat-profile-header-actions calibration-modern-actions">
            <CrcButton
              label="Download template"
              variant="tertiary"
              icon={LayoutTemplate}
              onClick={handleDownloadTemplate}
              size="small"
            />
            <CrcButton
              label="Download As Excel"
              variant="tertiary"
              icon={FileSpreadsheet}
              onClick={handleDownloadData}
              size="small"
              loading={downloadLoading}
            />
            <CrcButton
              label="Bulk Upload"
              variant="tertiary"
              icon={Upload}
              onClick={showBulkUploadModal}
              size="small"
            />
            <CrcButton label="Add New" variant="primary" icon={Plus} onClick={() => { setIsEdit(false); setMaterialRecord({}); setMaterialsFormModal(true); }} size="small" />
            {selectedRows.length > 0 && (
              <CrcButton
                label={
                  pdfGenerationLoading
                    ? 'Generating Report...'
                    : `Combined Report (${selectedRows.length})`
                }
                variant="secondary"
                onClick={() => {
                  if (!pdfGenerationLoading) {
                    handleGeneratePdf();
                  }
                }}
                size="small"
                disabled={pdfGenerationLoading}
              />
            )}
          </div>
        </div>
        <div className="addTeamDetails heat-profile-table-wrap">
          <Table
            columns={modernColumnsWithFilters}
            dataSource={paginatedDataForModern}
            rowKey="id"
            loading={{ indicator: <Spinner variant="inline" size="medium" />, spinning: isMaterialsLoading }}
            className="h-100 pipeline-table equipPaginationTable heat-profile-table"
            scroll={{ x: 2000 }}
            pagination={tablePaginationModern}
            rowSelection={{
              type: 'checkbox',
              selectedRowKeys: selectedRows.map(r => r.id),
              onChange: (selectedRowKeys, selectedRows) => {
                setSelectedRows(selectedRows);
              },
              columnWidth: 56,
              columnTitle: (() => {
                const currentPageIds = paginatedDataForModern.map((r) => r.id);
                const selectedOnPage = selectedRows.filter((r) => currentPageIds.includes(r.id));
                const allSelected = currentPageIds.length > 0 && selectedOnPage.length === currentPageIds.length;
                const indeterminate = selectedOnPage.length > 0 && selectedOnPage.length < currentPageIds.length;
                return (
                  <div className="materials-table-header-checkbox" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', paddingLeft: 8, paddingRight: 8, minWidth: 36, overflow: 'visible' }} onClick={(e) => e.stopPropagation()}>
                    <Checkbox
                      checked={allSelected}
                      indeterminate={indeterminate}
                      onChange={(checkedOrEvent) => {
                        const checked = typeof checkedOrEvent === 'boolean' ? checkedOrEvent : (checkedOrEvent?.target?.checked ?? false);
                        const currentIds = paginatedDataForModern.map((r) => r.id);
                        setSelectedRows((prev) => {
                          const prevOthers = prev.filter((r) => !currentIds.includes(r.id));
                          return checked ? [...prevOthers, ...paginatedDataForModern] : prevOthers;
                        });
                      }}
                      borderColor="#00C9A7"
                      aria-label="Select all rows on page"
                    />
                  </div>
                );
              })(),
              renderCell: (checked, record) => (
                <div className="materials-table-cell-checkbox" style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', paddingLeft: 8, paddingRight: 8, minWidth: 36, overflow: 'visible' }} onClick={(e) => e.stopPropagation()}>
                  <Checkbox
                    checked={!!checked}
                    onChange={(checkedOrEvent) => {
                      const newChecked = typeof checkedOrEvent === 'boolean' ? checkedOrEvent : (checkedOrEvent?.target?.checked ?? false);
                      if (newChecked) {
                        setSelectedRows((prev) => (prev.some((r) => r.id === record.id) ? prev : [...prev, record]));
                      } else {
                        setSelectedRows((prev) => prev.filter((r) => r.id !== record.id));
                      }
                    }}
                    borderColor="#00C9A7"
                    aria-label={`Select row ${record.id}`}
                  />
                </div>
              ),
            }}
          />
        </div>

        {materialsFormModal && (
          <MaterialsModal
            isModalVisible={materialsFormModal}
            setIsModalVisible={setMaterialsFormModal}
            materialForm={materialForm}
            isEdit={isEdit}
            setIsEdit={setIsEdit}
            editRecord={materialRecord}
            fetchMaterialsData={fetchMaterialsData}
          />
        )}

        <Modal
          className="heatprofile-modal"
          title="Upload Materials"
          centered
          open={bulkUploadModal}
          onCancel={handleCancelBulkUploadModal}
          footer={[
            <Button key="cancel" onClick={handleCancelBulkUploadModal}>
              Cancel
            </Button>,
            <Button key="apply" type="primary" onClick={handleBulkUpload} loading={uploadLoading}>
              {uploadLoading ? 'Uploading' : 'Upload'}
            </Button>,
          ]}
        >
          {renderMaterialsBulkUploadPicker()}
        </Modal>

        <Modal
          className="heatprofile-modal"
          title="Share Material"
          centered
          open={shareMaterialsModal}
          onCancel={handleCancelShareMaterialsModal}
          footer={[
            <Button key="cancel" onClick={handleCancelShareMaterialsModal}>
              Cancel
            </Button>,
            <Button key="apply" type="primary" onClick={shareMaterials}>
              Share
            </Button>,
          ]}
        >
          <div className="uploadFile_admin">
            <Input
              placeholder="Enter Emails Followed By Comma (,)"
              style={{ height: '40px', borderRadius: '8px' }}
              value={emailAddress}
              onChange={(e) => setEmailAddress(e.target.value)}
            />
            <div style={{ marginTop: '8px', fontSize: '12px', color: '#6b7280' }}>
              eg: 1234@mail.com, 234@email.com
            </div>
          </div>
        </Modal>
      </div>
    );
  }

  return (
    <div className="flex-auto">
      <div className="flex-auto">
        <div className="d-flex mb-3 align-items-center tab-content-title">
          <div className="back_heatp_modal">
            <Tooltip title={'Back'}>
              <ArrowLeftOutlined
                onClick={() => {
                  history.push('/calibration-control');
                }}
              />
            </Tooltip>
          </div>
          {/* <Title className="mb-0 add-new-calibration" level={3} style={{ paddingLeft: '20px' }}>
                                List of Calibration
                            </Title> */}

          <Typography
            className="project-title"
            style={{ fontSize: '25px', paddingLeft: '20px' }}
            level={4}
          >
            List of Materials
          </Typography>
          <div className="new-calibration new-project-team">
            <Text
              onClick={() => {
                setIsEdit(false);
                setMaterialRecord({});
                setMaterialsFormModal(true);
              }}
              className="ps-3 add-new-team"
            >
              <span className="icon-22">
                <svg>
                  <use xlinkHref="#add" />
                </svg>
              </span>
              Add New
            </Text>
            <Text
              onClick={handleDownloadTemplate}
              className="ps-3 add-new-team"
            >
              <span className="icon-22">
                <LayoutTemplate size={18} strokeWidth={2} aria-hidden />
              </span>
              Download template
            </Text>
            <Text
              onClick={handleDownloadData}
              className="ps-3 add-new-team"
              style={{
                cursor: downloadLoading ? 'not-allowed' : 'pointer',
                opacity: downloadLoading ? 0.6 : 1,
              }}
            >
              <span className="icon-22">
                <FileSpreadsheet size={18} strokeWidth={2} aria-hidden />
              </span>
              Download As Excel
            </Text>
            <Text
              onClick={showBulkUploadModal}
              className="ps-3 add-new-team"
            >
              <span className="icon-22">
                <Upload size={18} strokeWidth={2} aria-hidden />
              </span>
              Bulk Upload
            </Text>

            {selectedRows.length > 0 && (
              <Text
                onClick={() => {
                  if (!pdfGenerationLoading) {
                    handleGeneratePdf();
                  }
                }}
                className="ps-3 add-new-team"
                style={{
                  cursor: pdfGenerationLoading ? 'not-allowed' : 'pointer',
                  opacity: pdfGenerationLoading ? 0.6 : 1,
                  pointerEvents: pdfGenerationLoading ? 'none' : 'auto',
                }}
              >
                {pdfGenerationLoading
                  ? 'Generating Report...'
                  : `Combined Report (${selectedRows.length})`
                }
              </Text>
            )}
          </div>
        </div>
        <div className="custom-line" />
        {getPipelineTabpanes()}
      </div>

      <Modal
        className="heatprofile-modal"
        title="Upload Materials"
        centered
        open={bulkUploadModal}
        onCancel={handleCancelBulkUploadModal}
        footer={[
          <Button key="cancel" onClick={handleCancelBulkUploadModal}>
            Cancel
          </Button>,
          <Button key="apply" type="primary" onClick={handleBulkUpload} loading={uploadLoading}>
            {uploadLoading ? 'Uploading' : 'Upload'}
          </Button>,
        ]}
      >
        {renderMaterialsBulkUploadPicker()}
      </Modal>

      <Modal
        className="heatprofile-modal"
        title="Share Material"
        centered
        open={shareMaterialsModal}
        onCancel={handleCancelShareMaterialsModal}
        footer={[
          <Button key="cancel" onClick={handleCancelShareMaterialsModal}>
            Cancel
          </Button>,
          <Button key="apply" type="primary" onClick={shareMaterials}>
            Share
          </Button>,
        ]}
      >
        <div className="uploadFile_admin">
          <Input
            placeholder="Enter Emails Followed By Comma (,)"
            style={{ height: '40px', borderRadius: '8px' }}
            value={emailAddress}
            onChange={(e) => setEmailAddress(e.target.value)}
          />
          <div style={{ marginTop: '8px', fontSize: '12px', color: '#6b7280' }}>
            eg: 1234@mail.com, 234@email.com
          </div>
        </div>
      </Modal>

      {materialsFormModal && (
        <MaterialsModal
          isModalVisible={materialsFormModal}
          setIsModalVisible={setMaterialsFormModal}
          materialForm={materialForm}
          isEdit={isEdit}
          setIsEdit={setIsEdit}
          editRecord={materialRecord}
          fetchMaterialsData={fetchMaterialsData}
        />
      )}
    </div>
  );
};

const mapStateToProps = ({ materialsReducer }) => {
  const materialsData = get(materialsReducer, 'materialsData', []);
  const isMaterialsLoading = get(materialsReducer, 'isMaterialsLoading', false);
  const materialsCount = get(materialsReducer, 'materialsCount', 0);
  return {
    materialsData,
    isMaterialsLoading,
    materialsCount,
  };
};

const mapDispatchToProps = {
  onFetchMaterials: fetchMaterials,
  onBulkUploadMaterials: bulkUploadMaterials,
  onShareMaterial: shareMaterial,
};

export default connect(mapStateToProps, mapDispatchToProps)(MaterialsMainPage);
